/**
 * Accepting an invitation.
 *
 * The link is the whole credential, so the page shows only what its holder
 * already knows — the address it was sent to, who sent it, and what the server
 * calls itself — and nothing that would make a harvested link worth more than
 * the one account it can create.
 *
 * A link that cannot be used fails in three distinguishable ways, and each is
 * a different situation for the person holding it: already redeemed, expired,
 * or never valid. They are told which rather than being handed one shrug.
 *
 * The layout comes from `LoginPage`, which owns the shell every
 * unauthenticated screen wears; this route has no stylesheet of its own on
 * purpose, so the three of them cannot drift apart.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { useInvitationPreview } from '@/api/queries'
import type { AuthSession, FieldErrors, InvitationPreview, InvitationRole } from '@/api/types'
import { Button, FullPageSpinner, TextField } from '@/components/ui'
import { formatRelative } from '@/lib/format'

import {
  AuthCard,
  AuthCardBrand,
  AuthFooter,
  AuthForm,
  AuthHeading,
  AuthNotice,
  AuthShell,
  AuthSummary,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  describeRetry,
  firstFieldError,
  localTimezone,
  networkMessage,
  validateUsername,
} from './LoginPage'

// ── Why a link cannot be used ───────────────────────────────────────────────

type LinkFailure = 'accepted' | 'expired' | 'unknown' | 'rate_limited' | 'unreachable' | 'other'

/**
 * The server reports an expired link and an unrecognised one with the same
 * `not_found` code, separated only by the human message, so an explicit
 * `details.reason` is preferred where a server offers one and the message is
 * the fallback. Both are read defensively: neither is guaranteed.
 */
function classifyFailure(error: unknown): { kind: LinkFailure; message: string } {
  if (!isApiError(error)) return { kind: 'unreachable', message: networkMessage() }

  switch (error.code) {
    case 'conflict':
    case 'already_exists':
      return { kind: 'accepted', message: error.message }
    case 'rate_limited':
      return {
        kind: 'rate_limited',
        message: `Too many attempts from here. Try again ${describeRetry(error.retryAfterSeconds)}.`,
      }
    case 'not_found': {
      const reason: unknown = error.details['reason']
      const reasonText = typeof reason === 'string' ? reason : ''
      const expired = reasonText === 'expired' || /expired/i.test(error.message)

      return { kind: expired ? 'expired' : 'unknown', message: error.message }
    }
    default:
      return { kind: 'other', message: error.message }
  }
}

interface FailureCopy {
  title: string
  lede: string
}

function failureCopy(kind: LinkFailure): FailureCopy {
  switch (kind) {
    case 'accepted':
      return {
        title: 'This invitation has already been used',
        lede:
          'An account was created from this link. If it was you, sign in with it; if it was not, tell whoever sent the invitation.',
      }
    case 'expired':
      return {
        title: 'This invitation has expired',
        lede:
          'Invitations are good for a limited time. Ask whoever invited you to send a fresh link — it takes them a moment.',
      }
    case 'unknown':
      return {
        title: 'We do not recognise this invitation',
        lede:
          'The link may have been mistyped, cut short by an email client, or withdrawn. Ask for a new one and open it in full.',
      }
    case 'rate_limited':
      return {
        title: 'Too many attempts',
        lede: 'This server has paused invitation checks from here for a moment. Try again shortly.',
      }
    case 'unreachable':
      return {
        title: 'Could not reach the server',
        lede: 'The invitation may be fine. Check your connection and open the link again.',
      }
    case 'other':
      return {
        title: 'This invitation could not be checked',
        lede: 'The server refused the request. The message it gave is below.',
      }
  }
}

function describeRole(role: InvitationRole): string {
  switch (role) {
    case 'admin':
      return 'Administrator — can manage the server as well as take part'
    case 'guest':
      return 'Guest — limited to the rooms you are invited to'
    case 'member':
      return 'Member — full access to the rooms you join'
  }
}

// ── The accept form ─────────────────────────────────────────────────────────

interface AcceptFormProps {
  token: string
  invitation: InvitationPreview
}

function AcceptForm({ token, invitation }: AcceptFormProps): ReactElement {
  const navigate = useNavigate()
  const { refresh } = useAuth()

  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [serverErrors, setServerErrors] = useState<FieldErrors>({})

  const firstField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    firstField.current?.focus()
  }, [])

  const usernameProblem = useMemo(() => validateUsername(username), [username])
  const longEnough = password.length >= MIN_PASSWORD_LENGTH
  const tooLong = password.length > MAX_PASSWORD_LENGTH
  const matches = password !== '' && password === confirmation
  const needsEmail = invitation.email === null
  const validEmail = !needsEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim())

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      setSubmitted(true)
      setNotice(null)
      setServerErrors({})

      if (usernameProblem !== null || !validEmail || !longEnough || tooLong || !matches) {
        setNotice('Check the highlighted fields and try again.')
        return
      }

      setBusy(true)
      try {
        // Redeeming opens a session in the same request: the password was just
        // chosen here, so asking for it again on a login form would be asking
        // someone to prove what they have this second told us.
        await api.post<AuthSession>(
          `/invitations/${encodeURIComponent(token)}/accept`,
          {
            username,
            password,
            password_confirmation: confirmation,
            display_name: displayName.trim() === '' ? undefined : displayName.trim(),
            email: needsEmail ? email.trim() : undefined,
            timezone: localTimezone(),
          },
        )

        await refresh()
        navigate('/', { replace: true })
      } catch (caught) {
        if (isApiError(caught) && caught.code === 'validation_failed') {
          setServerErrors(caught.fieldErrors)
          setNotice('The server did not accept these details. See the messages below each field.')
        } else {
          setNotice(classifyFailure(caught).message)
        }
      } finally {
        setBusy(false)
      }
    },
    [
      confirmation,
      displayName,
      email,
      longEnough,
      matches,
      navigate,
      needsEmail,
      password,
      refresh,
      token,
      tooLong,
      username,
      usernameProblem,
      validEmail,
    ],
  )

  return (
    <AuthForm onSubmit={(event) => void onSubmit(event)}>
      {notice === null ? null : <AuthNotice tone="error">{notice}</AuthNotice>}

      <TextField
        ref={firstField}
        label="Username"
        name="username"
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        hint="Lowercase letters, digits, dot, dash or underscore. This is how people mention you."
        error={
          firstFieldError(serverErrors, 'username') ??
          (submitted || username !== '' ? usernameProblem : null)
        }
        required
      />

      <TextField
        label="Display name"
        name="name"
        autoComplete="name"
        value={displayName}
        onChange={(event) => setDisplayName(event.target.value)}
        hint="Your name as colleagues will read it. Leave it blank to use your username."
        error={firstFieldError(serverErrors, 'display_name')}
      />

      {needsEmail ? (
        <TextField
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          hint="Used for signing in and recovering your account."
          error={
            firstFieldError(serverErrors, 'email') ??
            (submitted && !validEmail ? 'Enter a valid email address.' : null)
          }
          required
        />
      ) : null}

      <TextField
        label="Password"
        name="new-password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters. A few words strung together works well.`}
        error={
          firstFieldError(serverErrors, 'password') ??
          (tooLong
            ? `That is longer than ${MAX_PASSWORD_LENGTH} characters.`
            : password !== '' && !longEnough
              ? `Too short — ${MIN_PASSWORD_LENGTH} characters minimum.`
              : null)
        }
        required
      />

      <TextField
        label="Confirm password"
        name="confirm-password"
        type="password"
        autoComplete="new-password"
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
        error={
          firstFieldError(serverErrors, 'password_confirmation') ??
          (confirmation !== '' && !matches ? 'The two passwords do not match.' : null)
        }
        required
      />

      <Button type="submit" variant="primary" fullWidth loading={busy}>
        Create account and join {invitation.server_display_name ?? invitation.server_id}
      </Button>
    </AuthForm>
  )
}

// ── The page ────────────────────────────────────────────────────────────────

export function InvitePage(): ReactElement {
  const { token } = useParams<{ token: string }>()
  const { user } = useAuth()
  const preview = useInvitationPreview(token)

  const serverName = preview.data?.server_display_name ?? preview.data?.server_id ?? null

  if (token === undefined || token === '') {
    const copy = failureCopy('unknown')

    return (
      <AuthShell>
        <AuthCard>
          <AuthHeading title={copy.title} lede={copy.lede} primary />
          <Link to="/login">Go to sign in</Link>
        </AuthCard>
      </AuthShell>
    )
  }

  if (preview.isError) {
    const { kind, message } = classifyFailure(preview.error)
    const copy = failureCopy(kind)

    return (
      <AuthShell>
        <AuthCard>
          <AuthHeading title={copy.title} lede={copy.lede} primary />
          <AuthNotice tone="error">{message}</AuthNotice>
          <Link to="/login">Go to sign in</Link>
        </AuthCard>
      </AuthShell>
    )
  }

  const invitation = preview.data

  if (invitation === undefined) {
    return <FullPageSpinner label="Checking your invitation" />
  }

  return (
    <AuthShell>
      <AuthCard>
        <AuthCardBrand logoUrl={invitation.server_logo_url} name={serverName} />
        <AuthHeading
          title={`Join ${serverName ?? 'this relaii server'}`}
          lede={
            invitation.invited_by === null
              ? 'You have been invited to this relaii server. Choose how you want to appear and set a password.'
              : `${invitation.invited_by} invited you to this relaii server. Choose how you want to appear and set a password.`
          }
          primary
        />

        <AuthSummary
          items={[
            { term: 'Server', value: invitation.server_id },
            ...(invitation.email === null
              ? []
              : [{ term: 'Invited', value: invitation.email }]),
            { term: 'Joining as', value: describeRole(invitation.role) },
            { term: 'Expires', value: formatRelative(invitation.expires_at) },
          ]}
        />

        {user === null ? null : (
          <AuthNotice tone="info">
            You are already signed in as {user.display_name}. Accepting this invitation creates a
            separate account and signs you out of that one.
          </AuthNotice>
        )}

        <AcceptForm token={token} invitation={invitation} />
      </AuthCard>

      <AuthFooter>
        relaii is self-hosted. This account exists only on {invitation.server_id}, and the people
        running it can see the same messages any chat server operator can.
      </AuthFooter>
    </AuthShell>
  )
}

// ── Entering a registration code ───────────────────────────────────────────

export function CodeRegistrationPage(): ReactElement {
  const navigate = useNavigate()
  const { server } = useAuth()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const codeField = useRef<HTMLInputElement>(null)

  useEffect(() => codeField.current?.focus(), [])

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const value = code.trim()
    if (value === '') {
      setError('Enter the registration code you received.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const invitation = await api.get<InvitationPreview>(
        `/invitations/${encodeURIComponent(value)}`,
      )
      if (invitation.kind !== 'code') {
        setError('That is an invitation link, not a registration code.')
        return
      }
      navigate(`/invite/${encodeURIComponent(value)}`)
    } catch (caught) {
      setError(classifyFailure(caught).message)
    } finally {
      setBusy(false)
    }
  }

  const serverName = server?.display_name ?? server?.server_id ?? null

  return (
    <AuthShell>
      <AuthCard>
        <AuthCardBrand logoUrl={server?.logo_url ?? null} name={serverName} />
        <AuthHeading
          title="Register with code"
          lede="Enter the code an administrator gave you. Once it is accepted, you can create your account."
        />
        <AuthForm onSubmit={(event) => void submit(event)}>
          {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}
          <TextField
            ref={codeField}
            label="Registration code"
            name="registration-code"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="ABCD-EFGH-JKLM"
            required
          />
          <Button type="submit" variant="primary" fullWidth loading={busy}>
            Continue
          </Button>
        </AuthForm>
        <Link to="/login">Back to sign in</Link>
      </AuthCard>
    </AuthShell>
  )
}
